import { useCallback, useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, Trash2, Upload } from 'lucide-react'

import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog'
import { useToast } from '../ui/toast'
import { cn } from '../../utils/cn'
import { deleteMediaAsset, getMediaAssets, MediaAsset, uploadMediaAsset } from '../../lib/api/mediaAssets'

type MediaPickerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (asset: MediaAsset) => void
}

const formatFileSize = (sizeBytes: number) => {
  if (!Number.isFinite(sizeBytes) || sizeBytes < 0) return ''
  if (sizeBytes < 1024) return `${sizeBytes} B`
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(0)} KB`
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
}

const extractErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: { detail?: string; message?: string } } })?.response?.data
  return data?.detail ?? data?.message ?? fallback
}

// Lets campaign editors pick an existing graphic from the shared media library or upload a new one.
export const MediaPickerDialog = ({ open, onOpenChange, onSelect }: MediaPickerDialogProps) => {
  const { addToast } = useToast()
  const [assets, setAssets] = useState<MediaAsset[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadAssets = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getMediaAssets()
      setAssets(data)
    } catch (error) {
      console.error('Failed to load media library', error)
      addToast({ title: 'Error', description: 'Could not load the media library.' })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!open) return

    setSelectedId(null)
    void loadAssets()
  }, [open, loadAssets])

  const handleSelect = (asset: MediaAsset) => {
    setSelectedId(asset.id)
    onSelect(asset)
    onOpenChange(false)
  }

  const handleUploadSelected = async (file: File | null) => {
    if (!file) return

    setUploading(true)
    try {
      const asset = await uploadMediaAsset(file)
      // The library is newest first, so the fresh upload leads the grid and is picked right away.
      setAssets((prev) => [asset, ...prev])
      handleSelect(asset)
    } catch (error) {
      console.error('Failed to upload media asset', error)
      addToast({
        title: 'Upload failed',
        description: extractErrorMessage(error, 'Could not upload the graphic.'),
        variant: 'error'
      })
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async (asset: MediaAsset) => {
    const confirmed = window.confirm(`Delete "${asset.fileName}" from the media library?`)
    if (!confirmed) return

    setDeletingId(asset.id)
    try {
      await deleteMediaAsset(asset.id)
      setAssets((prev) => prev.filter((item) => item.id !== asset.id))
      if (selectedId === asset.id) setSelectedId(null)
    } catch (error) {
      console.error('Failed to delete media asset', error)
      addToast({
        title: 'Delete failed',
        description: extractErrorMessage(error, 'Could not delete the graphic.'),
        variant: 'error'
      })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(96vw,720px)] p-6">
        <DialogTitle>Media Library</DialogTitle>
        <DialogDescription>
          Pick an existing graphic or upload a new one for this image block.
        </DialogDescription>

        <div className="mt-4 flex justify-end">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            aria-label="Upload new graphic file"
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null
              void handleUploadSelected(file)
            }}
          />
          <Button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Uploading…' : 'Upload new graphic'}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center rounded-lg border border-border py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : assets.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
            <ImagePlus className="h-6 w-6" />
            <p>The media library is empty. Upload your first graphic.</p>
          </div>
        ) : (
          <div className="mt-4 grid max-h-[50vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4">
            {assets.map((asset) => (
              <div
                key={asset.id}
                className={cn(
                  'group relative overflow-hidden rounded-md border transition-colors',
                  selectedId === asset.id ? 'border-primary ring-2 ring-primary' : 'border-border'
                )}
              >
                <button
                  type="button"
                  className="block w-full text-left"
                  aria-label={`Select ${asset.fileName}`}
                  onClick={() => handleSelect(asset)}
                >
                  <img src={asset.url} alt={asset.fileName} className="h-24 w-full bg-muted object-cover" />
                  <span className="block truncate px-2 py-1.5 text-xs text-muted-foreground" title={asset.fileName}>
                    {asset.fileName}
                    {formatFileSize(asset.sizeBytes) ? ` · ${formatFileSize(asset.sizeBytes)}` : ''}
                  </span>
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-7 w-7 p-0 opacity-80 group-hover:opacity-100"
                  aria-label={`Delete ${asset.fileName}`}
                  disabled={deletingId !== null}
                  onClick={() => void handleDelete(asset)}
                >
                  {deletingId === asset.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
